import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { BookHeart, KeyRound } from "lucide-react";
import { toast } from "sonner";

const Auth = () => {
  const [params] = useSearchParams();
  const [isRegister, setIsRegister] = useState(params.get("mode") === "register");
  const [form, setForm] = useState({ name: "", email: params.get("email") || "", password: "", birth_date: "", invite_code: "" });
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [gateEnabled, setGateEnabled] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const next = params.get("next") || "/ricordi";

  useEffect(() => { if (user) navigate(next); }, [user, navigate, next]);
  useEffect(() => { api.get("/access-gate/status").then((r) => setGateEnabled(!!r.data.enabled)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (isRegister && !accepted) {
      toast.error("Per registrarti devi accettare i Termini di Servizio e la Privacy.");
      return;
    }
    setLoading(true);
    try {
      if (isRegister) {
        await register(form);
        toast.success("Benvenuto! Il tuo libro è pronto.");
      } else {
        await login(form.email, form.password);
        toast.success("Bentornato.");
      }
      navigate(next);
    } catch (err) {
      const msg = err.response
        ? formatApiErrorDetail(err.response?.data?.detail)
        : "Impossibile contattare il server. Controlla la connessione e riprova.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/6274899/pexels-photo-6274899.jpeg" alt="Album" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#2C2A29]/45" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-serif text-3xl leading-tight">Ogni ricordo è un filo che ti tiene vicino a chi ami.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-in">
          <Link to="/" className="flex items-center gap-2 mb-8" data-testid="auth-logo">
            <BookHeart className="w-7 h-7 text-primary" />
            <span className="font-serif text-2xl">Eterna</span>
          </Link>
          <h1 className="font-serif text-3xl mb-1">{isRegister ? "Crea il tuo libro" : "Bentornato"}</h1>
          <p className="text-sm text-muted-foreground mb-8">{isRegister ? "Inizia a custodire ciò che conta." : "Accedi al tuo spazio privato."}</p>

          <form onSubmit={submit} className="space-y-4">
            {isRegister && (
              <div>
                <Label htmlFor="name">Nome</Label>
                <Input id="name" value={form.name} onChange={set("name")} required placeholder="Mario Rossi" className="mt-1.5 bg-[#F4F1EA] border-border rounded-xl h-11" data-testid="auth-name-input" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={set("email")} required placeholder="tu@esempio.it" className="mt-1.5 bg-[#F4F1EA] border-border rounded-xl h-11" data-testid="auth-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={form.password} onChange={set("password")} required placeholder="••••••••" className="mt-1.5 bg-[#F4F1EA] border-border rounded-xl h-11" data-testid="auth-password-input" />
            </div>
            {isRegister && (
              <div>
                <Label htmlFor="birth_date">Data di nascita (facoltativa)</Label>
                <Input id="birth_date" type="date" value={form.birth_date} onChange={set("birth_date")} className="mt-1.5 bg-[#F4F1EA] border-border rounded-xl h-11" data-testid="auth-birth-input" />
              </div>
            )}
            {isRegister && gateEnabled && (
              <div>
                <Label htmlFor="invite_code" className="flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5 text-primary" /> Codice d'invito</Label>
                <Input id="invite_code" value={form.invite_code} onChange={set("invite_code")} required placeholder="Inserisci il codice ricevuto" className="mt-1.5 bg-[#F4F1EA] border-border rounded-xl h-11" data-testid="auth-invite-input" />
                <p className="text-xs text-muted-foreground mt-1.5">Eterna è in fase di prova: la registrazione è su invito.</p>
              </div>
            )}
            {isRegister && (
              <label className="flex items-start gap-2.5 text-sm text-[#3d3a38] cursor-pointer select-none" data-testid="auth-terms-label">
                <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-primary shrink-0" data-testid="auth-terms-checkbox" />
                <span>
                  Accetto i <a href="/termini" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="auth-terms-link">Termini di Servizio</a> e ho letto l'<a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="auth-privacy-link">Informativa sulla Privacy</a>.
                </span>
              </label>
            )}
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-primary hover:bg-[#c9694f] text-white" data-testid="auth-submit-btn">
              {loading ? "Attendere…" : isRegister ? "Crea account" : "Accedi"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground text-center mt-6">
            {isRegister ? "Hai già un account? " : "Non hai un account? "}
            <button onClick={() => setIsRegister(!isRegister)} className="text-primary font-medium hover:underline" data-testid="auth-toggle-btn">
              {isRegister ? "Accedi" : "Registrati"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
